import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, FileText, Download, Calendar, Sparkles, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react'

interface Report {
  id: string
  name: string
  description?: string
  report_type: string
  config: any
  schedule?: string
  last_run?: number
  next_run?: number
  active: boolean
  create_date: number
}

export function ReportStudio() {
  const [reports, setReports] = useState<Report[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)

  // Form state for new report
  const [reportName, setReportName] = useState('')
  const [reportType, setReportType] = useState('fleet_summary')
  const [reportDescription, setReportDescription] = useState('')
  const [aiPrompt, setAiPrompt] = useState('')
  const [schedule, setSchedule] = useState('manual')
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    fetchReports()
  }, [])

  const fetchReports = async () => {
    try {
      const response = await fetch('/api/reports')
      const data = await response.json()
      setReports(data.reports || [])
    } catch (error) {
      console.error('Failed to fetch reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) return

    setGenerating(true)
    try {
      const response = await fetch('/api/reports/generate-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt }),
      })

      const data = await response.json()

      if (data.success) {
        // Pre-fill form with AI-generated config
        setReportName(data.report.name)
        setReportDescription(data.report.description)
        setReportType(data.report.type)
      }
    } catch (error) {
      console.error('AI generation failed:', error)
    } finally {
      setGenerating(false)
    }
  }

  const handleCreateReport = async () => {
    setCreating(true)
    try {
      const response = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: reportName,
          description: reportDescription,
          report_type: reportType,
          schedule: schedule !== 'manual' ? schedule : null,
          config: {
            // Report-specific config based on type
          },
        }),
      })

      const data = await response.json()

      if (data.success) {
        setReportName('')
        setReportDescription('')
        setAiPrompt('')
        fetchReports()
      }
    } catch (error) {
      console.error('Failed to create report:', error)
    } finally {
      setCreating(false)
    }
  }

  const handleRunReport = async (reportId: string) => {
    try {
      const response = await fetch(`/api/reports/${reportId}/run`, {
        method: 'POST',
      })

      const data = await response.json()

      if (data.success) {
        // Download the report
        if (data.downloadUrl) {
          window.open(data.downloadUrl, '_blank')
        }
        fetchReports()
      }
    } catch (error) {
      console.error('Failed to run report:', error)
    }
  }

  const getReportTypeInfo = (type: string) => {
    const types: Record<string, { label: string; description: string; icon: any }> = {
      fleet_summary: {
        label: 'Fleet Summary',
        description: 'Overview of fleet performance, utilization, and key metrics',
        icon: TrendingUp,
      },
      safety_analysis: {
        label: 'Safety Analysis',
        description: 'Driver safety scores, incidents, and compliance violations',
        icon: AlertCircle,
      },
      hos_compliance: {
        label: 'HOS Compliance',
        description: 'Hours of Service compliance and violations report',
        icon: CheckCircle,
      },
      maintenance_due: {
        label: 'Maintenance Due',
        description: 'Vehicles requiring maintenance, inspection schedules',
        icon: FileText,
      },
      custom: {
        label: 'Custom Report',
        description: 'Build a custom report with AI assistance',
        icon: Sparkles,
      },
    }
    return types[type] || types.custom
  }

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">Loading reports...</p>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          AI Report Studio
        </h2>
        <p className="text-muted-foreground mt-1">
          Generate intelligent fleet reports with AI assistance
        </p>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="create" className="space-y-6">
        <TabsList>
          <TabsTrigger value="create">Create Report</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
          <TabsTrigger value="history">Report History</TabsTrigger>
        </TabsList>

        {/* Create Report Tab */}
        <TabsContent value="create" className="space-y-6">
          {/* AI Assistant */}
          <Card className="p-6 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">AI Report Assistant</h3>
                <p className="text-sm text-muted-foreground">
                  Describe what you want to analyze and AI will help create the report
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Textarea
                placeholder="Example: 'Show me which drivers have the most safety incidents this month' or 'Create a maintenance forecast for the next quarter'"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={3}
                className="resize-none"
              />
              <Button
                onClick={handleGenerateWithAI}
                disabled={!aiPrompt.trim() || generating}
                className="w-full"
              >
                {generating ? (
                  <>
                    <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate with AI
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Manual Report Creation */}
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Or Create Manually</h3>

            <div className="space-y-4">
              {/* Report Name */}
              <div className="space-y-2">
                <Label htmlFor="report-name">Report Name *</Label>
                <Input
                  id="report-name"
                  placeholder="e.g., Weekly Fleet Summary"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                />
              </div>

              {/* Report Type */}
              <div className="space-y-2">
                <Label htmlFor="report-type">Report Type *</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(getReportTypeInfo('')).map(([key]) => {
                      const info = getReportTypeInfo(key)
                      const Icon = info.icon
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {info.label}
                          </div>
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {getReportTypeInfo(reportType).description}
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Optional description"
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  rows={2}
                />
              </div>

              {/* Schedule */}
              <div className="space-y-2">
                <Label htmlFor="schedule">Schedule</Label>
                <Select value={schedule} onValueChange={setSchedule}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Run Manually</SelectItem>
                    <SelectItem value="daily">Daily at 6 AM</SelectItem>
                    <SelectItem value="weekly">Weekly on Monday</SelectItem>
                    <SelectItem value="monthly">Monthly on 1st</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Create Button */}
              <Button
                onClick={handleCreateReport}
                disabled={!reportName.trim() || creating}
                className="w-full"
              >
                {creating ? 'Creating...' : 'Create Report'}
              </Button>
            </div>
          </Card>
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled" className="space-y-4">
          {reports.filter((r) => r.schedule && r.active).length === 0 ? (
            <Card className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-medium mb-2">No Scheduled Reports</h3>
              <p className="text-sm text-muted-foreground">
                Create a report with a schedule to see it here
              </p>
            </Card>
          ) : (
            reports
              .filter((r) => r.schedule && r.active)
              .map((report) => (
                <Card key={report.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <h3 className="font-medium">{report.name}</h3>
                        <Badge variant="secondary">
                          {report.schedule}
                        </Badge>
                      </div>
                      {report.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {report.description}
                        </p>
                      )}
                      <div className="text-xs text-muted-foreground">
                        <span>Last run: {report.last_run ? new Date(report.last_run * 1000).toLocaleString() : 'Never'}</span>
                        {report.next_run && (
                          <>
                            {' • '}
                            <span>Next run: {new Date(report.next_run * 1000).toLocaleString()}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRunReport(report.id)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Run Now
                    </Button>
                  </div>
                </Card>
              ))
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card className="p-6">
            <p className="text-sm text-muted-foreground text-center">
              Report history will show recent report generations and downloads
            </p>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
